import { Clock, MessageSquare, Users } from 'lucide-react'
import { Link } from 'react-router-dom'

import type { RecipeList } from '@/api/types'
import { fr } from '@/i18n/fr'

export function RecipeCard({ recipe }: { recipe: RecipeList }) {
  return (
    <Link
      to={`/recipes/${recipe.id}`}
      className="group flex flex-col overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm transition-shadow hover:shadow-md dark:border-stone-800 dark:bg-stone-900"
    >
      <div className="aspect-[4/3] w-full bg-stone-100 dark:bg-stone-800">
        {/* The 600px copy, not the 1600px master (NFR-41): a page of 24 cards would
            otherwise pull several MB to draw thumbnails. The fallback covers a row that
            somehow has one and not the other. */}
        {(recipe.photo_card ?? recipe.photo) && (
          <img
            src={recipe.photo_card ?? recipe.photo ?? undefined}
            alt=""
            loading="lazy"
            className="h-full w-full object-cover"
          />
        )}
      </div>
      <div className="flex flex-1 flex-col gap-2 p-4">
        <h2 className="font-medium leading-snug text-stone-900 group-hover:underline dark:text-stone-100">
          {recipe.title}
        </h2>
        <div className="mt-auto flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-stone-500 dark:text-stone-400">
          {/* Each fact carries its own icon, so no number can be read as belonging to the
              one beside it. That is the M4 QA remark: "6 pers." next to the new comment
              count read as "6 people commented", when it has been the portions since M1.
              The words moved into the aria-labels, which is where they were doing the work
              anyway — nobody needed "pers." to know what a number beside a fork means. */}
          {recipe.total_time_min != null && (
            <Fact
              icon={<Clock className="h-3.5 w-3.5" aria-hidden />}
              value={fr.common.duration(recipe.total_time_min)}
              label={fr.common.durationSpoken(recipe.total_time_min)}
            />
          )}
          {recipe.servings != null && (
            <Fact
              icon={<Users className="h-3.5 w-3.5" aria-hidden />}
              value={recipe.servings}
              label={fr.recipeList.cardServings(recipe.servings)}
            />
          )}
          {/* F-19: a note left on a recipe is only useful to someone who doesn't already
              know it is there. Absent entirely from a recipe nobody has commented on. */}
          {recipe.comment_count > 0 && (
            <Fact
              icon={<MessageSquare className="h-3.5 w-3.5" aria-hidden />}
              value={recipe.comment_count}
              label={fr.recipeList.cardComments(recipe.comment_count)}
            />
          )}
        </div>
        {recipe.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {recipe.tags.map((tag) => (
              <span
                key={tag}
                className="rounded-full bg-stone-100 px-2 py-0.5 text-xs text-stone-600 dark:bg-stone-800 dark:text-stone-400"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>
    </Link>
  )
}

/**
 * One glyph and one number. The name is in the `aria-label` and nowhere else, so the label
 * is the only description that exists — it is not decoration duplicating visible text.
 */
function Fact({
  icon,
  value,
  label,
}: {
  icon: React.ReactNode
  value: string | number
  label: string
}) {
  return (
    <span className="inline-flex items-center gap-1" aria-label={label}>
      {icon}
      {value}
    </span>
  )
}
